import React, { useEffect, useState } from "react";
import {
  Autocomplete,
  Avatar,
  Box,
  FormControl,
  Grid,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import medal from "../../assets/images/medal.png";
import first from "../../assets/images/first.png";
import second from "../../assets/images/second.png";
import third from "../../assets/images/third.png";
import api from "../../api/client";
import { TableNodata } from "../../components/TableNodata";
import { useChangeableItems, getLabelByKey } from "../../store/useDynamicLabel";
import { TruncatedText } from "../../components/TruncatedText";
import { formatDate } from "../../utils/formatDate";
import { TopPerformersSkeleton } from "./DashboardCardSkeletons";

// Same list/filter sizes as the Upcoming Assessments card, so both cards line up
const LIST_HEIGHT = 358;
const FILTER_ROW_HEIGHT = 40;
// Number of performers asked for (Institute Admin / Trainer / Student)
const TOP_PERFORMERS_LIMIT = 10;

// test_type values accepted by /GetTopPerformers
const TEST_TYPES = {
  ALL: "ALL",
  PROCTORED: "PROCTORED",
  SELF_ASSESSMENT: "SELF_ASSESSMENT",
};

const medals = [first, second, third];

// Super Admin gets institutes -> top_performers (flattened here, keeping the institute);
// other roles get a flat list. Sorted by percentage so the medals go to the real top 3.
const parsePerformers = (responseData) => {
  const list = Array.isArray(responseData?.institutes)
    ? responseData.institutes.flatMap((inst) =>
      (inst?.top_performers || []).map((p) => ({
        ...p,
        institute_id: inst?.institute_id,
        institute_name: inst?.institute_name,
      }))
    )
    : responseData?.top_performers || responseData?.student_scores || responseData?.data || [];
  return [...list].sort((a, b) => (getScore(b) ?? -1) - (getScore(a) ?? -1));
};

// Score shown on the right: percentage (new API), falling back to final_score / mark
const getScore = (item) => item?.percentage ?? item?.final_score ?? item?.mark ?? null;

const formatScore = (item) => {
  if (item?.percentage != null) return `${+Number(item.percentage).toFixed(2)}%`;
  const score = item?.final_score ?? item?.mark;
  return score != null ? Math.round(score) : 0;
};

const avatarStyle = {
  bgcolor: "#e8f2ff",
  color: "#1e88e5",
  width: 24,
  height: 24,
  fontWeight: "bold",
  border: "2px solid #bbdefb",
};

/**
 * TopPerformers component
 *
 * "Top Performers" dashboard card (/GetTopPerformers).
 *   Super Admin:                        {} or { institute_id } — Institute dropdown, proctored assessments.
 *   Institute Admin / Trainer / Student: { test_type, limit } — All / Proctored / Self Assessment;
 *   the backend scopes the list from the login token (own institute / trainer's batches / student's completed assessments).
 *
 * @param {boolean} showInstituteFilter - Super Admin mode: Institute dropdown, sends institute_id. Defaults to false.
 * @param {Array} institutes - Dropdown options ({ institute_id, name }) when showInstituteFilter is true.
 * @param {boolean} showTestTypeFilter - Show the All / Proctored / Self Assessment filter. Defaults to false.
 * @param {Function} canOpen - (item) => boolean: whether clicking a row opens that exam result. Defaults to always.
 *
 * @returns {JSX.Element} The Top Performers card.
 */
const TopPerformers = ({
  showInstituteFilter = false,
  institutes = [],
  showTestTypeFilter = false,
  canOpen = () => true,
}) => {
  const navigate = useNavigate();
  const changeableItems = useChangeableItems();
  const getLabel = (itemKey) => getLabelByKey(changeableItems, itemKey);

  // Super Admin: institute picked in the dropdown (null = all institutes)
  const [selectedInstituteId, setSelectedInstituteId] = useState(null);
  // Institute Admin / Trainer / Student: test type
  const [testType, setTestType] = useState(TEST_TYPES.ALL);
  const [performers, setPerformers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Ignore a response that arrives after the filters have changed again
    let ignore = false;
    const fetchTopPerformers = async () => {
      setLoading(true);
      try {
        const payload = showInstituteFilter
          ? selectedInstituteId
            ? { institute_id: selectedInstituteId }
            : {}
          : { test_type: testType, limit: TOP_PERFORMERS_LIMIT };
        const res = await api.post("/GetTopPerformers", payload);
        if (ignore) return;
        if (res?.data?.status === "success") {
          setPerformers(parsePerformers(res.data));
        } else {
          setPerformers([]);
        }
      } catch (error) {
        if (!ignore) setPerformers([]);
        console.log("Error : ", error);
      } finally {
        if (!ignore) setLoading(false);
      }
    };
    fetchTopPerformers();
    return () => {
      ignore = true;
    };
  }, [showInstituteFilter, selectedInstituteId, testType]);


  // Super Admin institute dropdown: "All" first (all institutes), then every institute
  const instituteOptions = [
    { institute_id: null, name: `All ${getLabel("INSTITUTE")}` },
    ...institutes,
  ];

  const listHeight = LIST_HEIGHT;

  return (
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Grid
        container
        direction="column"
        sx={{
          borderRadius: "8px",
          border: "1px solid #c1c1c6ff",
          background: "#fff",
          boxShadow: "0px 2px 8px rgba(0,0,0,0.08)",
          p: 2,
        }}
      >
        {/* Header: title (+ test type filter on the right) */}
        <Grid
          sx={{
            display: "flex",
            gap: 1,
            mb: 1,
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: showInstituteFilter ? "nowrap" : "wrap",
          }}
        >
          <Grid sx={{ display: "flex", gap: 1, alignItems: "center", flexShrink: 0 }}>
            <Box
              component="img"
              src={medal}
              alt="medal"
              sx={{ width: 30, height: 30 }}
            />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Top Performers
            </Typography>
          </Grid>

          {showInstituteFilter && (
            <Autocomplete
              size="small"
              sx={{
                width: 280,
                minWidth: 0,
                flexShrink: 1,
                "& .MuiOutlinedInput-root": { height: FILTER_ROW_HEIGHT },
              }}
              options={instituteOptions}
              getOptionLabel={(option) => option?.name || ""}
              isOptionEqualToValue={(option, value) => option.institute_id === value.institute_id}
              value={
                instituteOptions.find((institute) => institute.institute_id === selectedInstituteId) ||
                instituteOptions[0]
              }
              onChange={(event, newValue) => setSelectedInstituteId(newValue?.institute_id ?? null)}
              disableClearable
              renderOption={(props, option) => {
                const { key, ...optionProps } = props;
                return (
                  <li key={key} {...optionProps}>
                    {/* Long institute names: "..." + full name on hover */}
                    <TruncatedText text={option.name} sx={{ width: "100%" }} />
                  </li>
                );
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  size="small"
                  variant="outlined"
                  sx={{ "& .MuiInputBase-input": { fontSize: "12px" } }}
                />
              )}
            />
          )}
          {showTestTypeFilter && (
            <FormControl size="small" sx={{ width: 150, flexShrink: 0 }}>
              <Select
                value={testType}
                onChange={(e) => setTestType(e.target.value)}
                sx={{ fontSize: "12px", height: FILTER_ROW_HEIGHT }}
              >
                <MenuItem value={TEST_TYPES.ALL} sx={{ fontSize: "12px" }}>
                  All
                </MenuItem>
                <MenuItem value={TEST_TYPES.PROCTORED} sx={{ fontSize: "12px" }}>
                  Proctored
                </MenuItem>
                <MenuItem value={TEST_TYPES.SELF_ASSESSMENT} sx={{ fontSize: "12px" }}>
                  Self Assessment
                </MenuItem>
              </Select>
            </FormControl>
          )}
        </Grid>


        {/* Performers List */}
        <Grid
          sx={{
            overflowY: "auto",
            maxHeight: listHeight,
            minHeight: listHeight,
            pr: 1,
          }}
        >
          {loading ? (
            <TopPerformersSkeleton />
          ) : performers.length === 0 ? (
            <Grid
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "100%",
              }}
            >
              <TableNodata />
            </Grid>
          ) : (
            performers.map((item, index) => {
              // The exam result page needs the attempt; rows without attempt_id can't be opened
              const clickable = !!item?.attempt_id && canOpen(item);
              return (
                <Grid
                  container
                  alignItems="center"
                  key={`top-performer-${index}`}
                  sx={{
                    mb: 1,
                    p: 1,
                    borderRadius: 2,
                    flexWrap: "nowrap",
                    cursor: clickable ? "pointer" : "default",
                    "&:hover": { backgroundColor: "#f5f5f5" },
                  }}
                  onClick={() => {
                    if (!clickable) return;
                    navigate("/dashboard/examOverview", {
                      state: {
                        assessmentId: item?.assessment_id,
                        attemptId: item?.attempt_id,
                        studentId: item?.user_id,
                        studentName: item?.student_name,
                      },
                    });
                  }}
                >
                  {/* Avatar + Name */}
                  <Grid
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      width: "35%",
                      minWidth: 0,
                    }}
                  >
                    <Avatar sx={{ ...avatarStyle, flexShrink: 0 }}>
                      {item?.student_name?.charAt(0)}
                    </Avatar>
                    <TruncatedText text={item?.student_name} sx={{ fontWeight: 500 }} />
                  </Grid>

                  {/* Test type / institute + category / completed date (whatever the response has) */}
                  <Grid sx={{ width: "50%", minWidth: 0 }}>
                    <TruncatedText text={item?.test_type || item?.institute_name} />
                    {item?.category ? (
                      <TruncatedText
                        text={item.category}
                        sx={{ fontSize: "12px", color: "#1e88e5" }}
                      />
                    ) : (
                      item?.completed_at && (
                        <TruncatedText
                          text={`Completed ${formatDate(item.completed_at)}`}
                          sx={{ fontSize: "12px", color: "#1e88e5" }}
                        />
                      )
                    )}
                    {item?.test_type && item?.institute_name && (
                      <TruncatedText
                        text={item.institute_name}
                        sx={{ fontSize: "12px", color: "text.secondary" }}
                      />
                    )}
                  </Grid>

                  {/* Score + medal for the top 3 */}
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "flex-end",
                      gap: 1,
                      width: "15%",
                    }}
                  >
                    <Typography sx={{ fontWeight: "bold", whiteSpace: "nowrap" }}>
                      {formatScore(item)}
                    </Typography>
                    {medals[index] && (
                      <Box
                        component="img"
                        src={medals[index]}
                        alt={`Rank ${index + 1}`}
                        sx={{ width: 26, height: 26 }}
                      />
                    )}
                  </Box>
                </Grid>
              );
            })
          )}
        </Grid>
      </Grid>
    </Box>
  );
};

export default TopPerformers;
